"use client"

import { useState, useEffect, useRef, useCallback, useMemo } from "react"
import {
  type SubAgent,
  type AgentDemoStep,
  type MainToolCall,
} from "@/lib/agent-demo-data"
import {
  SCENARIO_SKILLS_MAP,
  type ScenarioSkill,
} from "@/lib/demo-scenarios/scenario-skills"

function cloneDeep<T>(obj: T): T {
  return structuredClone(obj)
}

export interface UseAgentPlaybackReturn {
  visibleSteps: AgentDemoStep[]
  liveSubAgents: SubAgent[]
  mainToolsState: (MainToolCall & { status: "running" | "done" })[]
  allSubAgentsDone: boolean
  isRunning: boolean
  skillsLoaded: boolean
  setSkillsLoaded: (v: boolean) => void
  showInput: boolean
  selectedAgentId: string | null
  setSelectedAgentId: (id: string | null) => void
  selectedAgent: SubAgent | null
  agentStartTimes: Record<string, number>
  showPublishModal: boolean
  setShowPublishModal: (v: boolean) => void
  showShareToast: boolean
  setShowShareToast: (v: boolean) => void
  startDemo: () => void
  resetAll: () => void
  scrollRef: React.RefObject<HTMLDivElement>
  userPickedRef: React.MutableRefObject<boolean>
  scenarioSkills: ScenarioSkill[]
  handleScroll: () => void
}

export function useAgentPlayback(
  demoSteps: AgentDemoStep[],
  selectedScenarioId?: string,
  initialQuery = "",
): UseAgentPlaybackReturn {
  const [visibleSteps, setVisibleSteps] = useState<AgentDemoStep[]>([])
  const [liveSubAgents, setLiveSubAgents] = useState<SubAgent[]>([])
  const [mainToolsState, setMainToolsState] = useState<(MainToolCall & { status: "running" | "done" })[]>([])
  const [allSubAgentsDone, setAllSubAgentsDone] = useState(false)
  const [isRunning, setIsRunning] = useState(false)
  const [skillsLoaded, setSkillsLoaded] = useState(false)
  const [showInput, setShowInput] = useState(true)
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null)
  const [agentStartTimes, setAgentStartTimes] = useState<Record<string, number>>({})
  const [showPublishModal, setShowPublishModal] = useState(false)
  const [showShareToast, setShowShareToast] = useState(false)

  const scenarioSkills = useMemo(
    () => (selectedScenarioId ? SCENARIO_SKILLS_MAP[selectedScenarioId]?.skills ?? [] : []),
    [selectedScenarioId]
  )

  const scrollRef = useRef<HTMLDivElement>(null!)
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([])
  const userPickedRef = useRef(false)
  const userScrolledUpRef = useRef(false)

  // Only auto-scroll if user hasn't scrolled up
  const scrollToBottom = useCallback(() => {
    if (scrollRef.current && !userScrolledUpRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" })
    }
  }, [])

  // Detect if user is scrolled up
  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight
    userScrolledUpRef.current = distanceFromBottom > 80
  }, [])

  useEffect(() => { scrollToBottom() }, [visibleSteps, liveSubAgents, mainToolsState, scrollToBottom])

  useEffect(() => {
    return () => { timersRef.current.forEach(clearTimeout) }
  }, [])

  const selectedAgent = useMemo(
    () => liveSubAgents.find((a) => a.id === selectedAgentId) ?? null,
    [liveSubAgents, selectedAgentId]
  )

  // Auto-select first agent only
  useEffect(() => {
    if (userPickedRef.current) return
    if (selectedAgentId) return
    if (liveSubAgents.length === 1 && liveSubAgents[0].status === "running") {
      setSelectedAgentId(liveSubAgents[0].id)
      setShowPublishModal(false)
    }
  }, [liveSubAgents, selectedAgentId])

  /* ── Animate main tools ── */
  const animateMainTools = useCallback((tools: MainToolCall[]) => {
    tools.forEach((tool, i) => {
      const t1 = setTimeout(() => {
        setMainToolsState((prev) => [...prev, { ...tool, status: "running" }])
        const t2 = setTimeout(() => {
          setMainToolsState((prev) =>
            prev.map((t, idx) => (idx === i ? { ...t, status: "done" } : t))
          )
        }, tool.duration)
        timersRef.current.push(t2)
      }, tool.delay)
      timersRef.current.push(t1)
    })
  }, [])

  /* ── Animate subagents ── */
  const animateSubAgents = useCallback((agents: SubAgent[]) => {
    agents.forEach((agent) => {
      const t1 = setTimeout(() => {
        const cloned = cloneDeep(agent)
        cloned.status = "running"
        cloned.skills.forEach((s) => (s.status = "pending"))
        setAgentStartTimes((prev) => ({ ...prev, [agent.id]: Date.now() }))
        setLiveSubAgents((prev) => [...prev, cloned])

        agent.skills.forEach((skill, si) => {
          const t2 = setTimeout(() => {
            setLiveSubAgents((prev) =>
              prev.map((a) => {
                if (a.id !== agent.id) return a
                const u = cloneDeep(a)
                u.skills[si].status = "running"
                return u
              })
            )

            skill.toolCalls.forEach((tc, ti) => {
              const t3 = setTimeout(() => {
                setLiveSubAgents((prev) =>
                  prev.map((a) => {
                    if (a.id !== agent.id) return a
                    const u = cloneDeep(a)
                    u.skills[si].toolCalls[ti].status = tc.tool.includes("wait") ? "waiting" : "running"
                    return u
                  })
                )

                const baseDelay = tc.category === "feishu" && tc.tool.includes("wait") ? 2200 : 1400
                const finishDelay = baseDelay + Math.floor(Math.random() * 800)
                const t4 = setTimeout(() => {
                  setLiveSubAgents((prev) =>
                    prev.map((a) => {
                      if (a.id !== agent.id) return a
                      const u = cloneDeep(a)
                      u.skills[si].toolCalls[ti].status = "done"
                      if (u.skills[si].toolCalls.every((t) => t.status === "done")) u.skills[si].status = "done"
                      if (u.skills.every((s) => s.status === "done")) u.status = "done"
                      return u
                    })
                  )
                  setLiveSubAgents((prev) => {
                    if (prev.every((a) => a.status === "done") && prev.length === agents.length) {
                      setAllSubAgentsDone(true)
                    }
                    return prev
                  })
                }, finishDelay)
                timersRef.current.push(t4)
              }, tc.delay)
              timersRef.current.push(t3)
            })
          }, skill.delay)
          timersRef.current.push(t2)
        })
      }, agent.delay)
      timersRef.current.push(t1)
    })
  }, [])

  // Show summary + follow-up reactively once all subagents are done
  const summaryShownRef = useRef(false)
  useEffect(() => {
    if (allSubAgentsDone && !summaryShownRef.current) {
      summaryShownRef.current = true
      const summaryStep = demoSteps.find((s) => s.type === "summary")
      const followUpStep = demoSteps.find((s) => s.type === "follow-up")
      if (summaryStep) {
        const t1 = setTimeout(() => {
          setVisibleSteps((prev) => [...prev, summaryStep])
          userScrolledUpRef.current = false // scroll to show report
        }, 1500)
        timersRef.current.push(t1)
      }
      if (followUpStep) {
        const t2 = setTimeout(() => {
          setVisibleSteps((prev) => [...prev, followUpStep])
          setIsRunning(false)
        }, 5000)
        timersRef.current.push(t2)

        // Show share toast 2s after follow-ups appear
        const t3 = setTimeout(() => {
          setShowShareToast(true)
        }, 7000)
        timersRef.current.push(t3)
      }
    }
  }, [allSubAgentsDone, demoSteps])

  /* ── Start ── */
  const deferredBaseRef = useRef(0)

  const startDemo = useCallback(() => {
    timersRef.current.forEach(clearTimeout)
    timersRef.current = []
    setVisibleSteps([])
    setLiveSubAgents([])
    setMainToolsState([])
    setAllSubAgentsDone(false)
    setIsRunning(true)
    setSkillsLoaded(false)
    setShowInput(false)
    setSelectedAgentId(null)
    setAgentStartTimes({})
    setShowShareToast(false)
    userPickedRef.current = false
    summaryShownRef.current = false
    userScrolledUpRef.current = false

    // Phase 1: schedule only user + agent-reply steps immediately
    const initialTypes = new Set(["user", "agent-reply"])
    let maxInitialDelay = 0
    demoSteps.forEach((step) => {
      if (step.type === "summary" || step.type === "follow-up") return
      if (!initialTypes.has(step.type)) return
      if (step.delay > maxInitialDelay) maxInitialDelay = step.delay
      const t = setTimeout(() => {
        setVisibleSteps((prev) => [...prev, step])
      }, step.delay)
      timersRef.current.push(t)
    })
    deferredBaseRef.current = maxInitialDelay

    // If no scenario skills, load deferred steps immediately
    if (scenarioSkills.length === 0) {
      setSkillsLoaded(true)
    }
  }, [animateMainTools, animateSubAgents, demoSteps, scenarioSkills.length])

  // Phase 2: when skills finish loading, schedule deferred steps
  useEffect(() => {
    if (!skillsLoaded || !isRunning) return
    const initialTypes = new Set(["user", "agent-reply"])

    demoSteps.forEach((step) => {
      if (step.type === "summary" || step.type === "follow-up") return
      if (initialTypes.has(step.type)) return

      // Offset deferred steps relative to when skills finished
      const adjustedDelay = Math.max(0, step.delay - deferredBaseRef.current) + 300 // 300ms buffer after skills done
      const t = setTimeout(() => {
        if (step.type === "main-tools") {
          setVisibleSteps((prev) => [...prev, step])
          animateMainTools(step.mainTools!)
        } else if (step.type === "subagents") {
          setVisibleSteps((prev) => [...prev, step])
          animateSubAgents(step.subAgents!)
        } else {
          setVisibleSteps((prev) => [...prev, step])
        }
      }, adjustedDelay)
      timersRef.current.push(t)
    })
  }, [skillsLoaded, isRunning, demoSteps, animateMainTools, animateSubAgents, scenarioSkills.length])

  /* ── Reset ── */
  const resetAll = useCallback(() => {
    timersRef.current.forEach(clearTimeout)
    timersRef.current = []
    setVisibleSteps([])
    setLiveSubAgents([])
    setMainToolsState([])
    setAllSubAgentsDone(false)
    setIsRunning(false)
    setSkillsLoaded(false)
    setShowInput(true)
    setSelectedAgentId(null)
    setAgentStartTimes({})
    setShowShareToast(false)
    userPickedRef.current = false
    summaryShownRef.current = false
    userScrolledUpRef.current = false
  }, [])

  // Backtick key to reset
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "`") {
        e.preventDefault()
        resetAll()
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [resetAll])

  // Auto-start if initialQuery is provided (from landing page).
  useEffect(() => {
    if (initialQuery) {
      const t = setTimeout(() => startDemo(), 300)
      return () => clearTimeout(t)
    }
  }, [initialQuery, startDemo])

  return {
    visibleSteps,
    liveSubAgents,
    mainToolsState,
    allSubAgentsDone,
    isRunning,
    skillsLoaded,
    setSkillsLoaded,
    showInput,
    selectedAgentId,
    setSelectedAgentId,
    selectedAgent,
    agentStartTimes,
    showPublishModal,
    setShowPublishModal,
    showShareToast,
    setShowShareToast,
    startDemo,
    resetAll,
    scrollRef,
    userPickedRef,
    scenarioSkills,
    handleScroll,
  }
}
